import {
  Container,
  Row,
  Col,
  Button,
  CaptionText,
  MoveIcon,
} from '@paypalcorp/pp-react'
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useRef,
  useState,
} from 'react'
import Cropper from 'react-easy-crop'
import * as styles from './imageCropper.css'
import {
  ALLOWED_IMAGE_TYPES,
  COVER_IMAGE_MAX_TOTAL_SIZE,
  coverImageErrorCodeMapping,
} from '../../../constants'
import { useLocalizedContent } from '../../../hooks/useLocalizedContent'
import {
  imageFileReader,
  readAsDataURLAsync,
} from '../../../utils/image/image-file-reader'
import { Message } from '@paypalcorp/worldready-react'
import { ProposedPhoto } from '../../../../typings'

interface Area {
  width: number
  height: number
  x: number
  y: number
}

interface Props {
  proposedPhoto: ProposedPhoto
  setProposedPhoto: Dispatch<SetStateAction<ProposedPhoto>>
  onSave: ({ url, name }: { url: string; name: string }) => Promise<void>
  onError: (error: Error) => void
}

export const ImageCropper = ({
  proposedPhoto,
  setProposedPhoto,
  onSave,
  onError,
}: Props) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const croppedAreaPixelsRef = useRef<Area>()
  const fileInputRef = useRef<HTMLInputElement>()
  const { getContent } = useLocalizedContent()
  const allowedImageTypes = Object.keys(ALLOWED_IMAGE_TYPES)
    .map((key) => key)
    .join(',')

  const onCropComplete = async (
    _croppedArea: Area,
    croppedAreaPixels: Area,
  ) => {
    croppedAreaPixelsRef.current = croppedAreaPixels
  }

  const handleClickOnSave = async () => {
    try {
      const fileHandle = await imageFileReader({
        files: [proposedPhoto.file],
        restrictedTotalSize: COVER_IMAGE_MAX_TOTAL_SIZE,
        pixelCrop: croppedAreaPixelsRef.current,
      })
      fileHandle.name = proposedPhoto.name
      await onSave(fileHandle)
      return setProposedPhoto(undefined)
    } catch (err) {
      const errorMessage = coverImageErrorCodeMapping[err.code]
      if (errorMessage) {
        return onError(new Error(getContent(errorMessage)))
      }
      return onError(err)
    }
  }

  const handleZoom = (e: ChangeEvent<HTMLInputElement>) => {
    setZoom(parseFloat(e.target.value))
  }

  const handleClickChangePhoto = () => {
    fileInputRef.current?.click()
  }

  const onChangeImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedImage = e.target.files[0]
    const fileReadData: any = await readAsDataURLAsync(selectedImage)
    const url = fileReadData.data as string
    setProposedPhoto({ url, name: selectedImage.name, file: selectedImage })
  }

  return (
    <Container
      form
      className={styles.mainContainer}
      data-testid="cropPhotoScreen"
    >
      <Row>
        <Col form="full">
          <Cropper
            classes={{ containerClassName: styles.cropContainer }}
            image={proposedPhoto.url}
            crop={crop}
            zoom={zoom}
            aspect={2.32}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </Col>
      </Row>
      <Row>
        <Col form="full">
          <div className={styles.positionText}>
            <MoveIcon size="xs" />
            <CaptionText>
              <Message id="coverImage.crop.dragText" />
            </CaptionText>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <label htmlFor="changeImage" className={styles.controls}>
            <Button
              data-testid="changeImageButton"
              variant="tertiary"
              onClick={handleClickChangePhoto}
            >
              <Message id="coverImage.crop.changePhoto" />
            </Button>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedImageTypes}
            data-testid="changeImage"
            id="changeImage"
            className={styles.fileInput}
            onChange={onChangeImage}
          />
        </Col>
      </Row>
      <Row>
        <Col form="full">
          <input
            name="scale"
            type="range"
            value={zoom}
            onChange={handleZoom}
            min={1}
            max={3}
            step={0.1}
            aria-label={getContent('accessibility.slider')}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            data-testid="saveCroppedPhotoButton"
            onClick={handleClickOnSave}
          >
            <Message id="coverImage.crop.saveButton" />
          </Button>
        </Col>
      </Row>
    </Container>
  )
}
